/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext, useRef } from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import { Table as BTable } from 'react-bootstrap';
// @ts-ignore
import isEqual from 'lodash/isEqual';
import { faSquarePlus, faTrashCan, faFloppyDisk, faClone, faCopy } from '@fortawesome/free-regular-svg-icons';
import { faPencil, faRotateLeft, faXmark, faVial, faArrowLeft, faSkullCrossbones } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fingerprint, WsRpcError, getFingerprint, setFingerprint, createFingerprint, deleteFingerprint, Category, FingerMatchEntry, getCategories, getSchemas, getMatchesForFinger, setMatchesForFinger, getProbesForFinger, queueAiProbe,
    getSetting, setSetting,
    execControl
 }
       from '../data'
import { RowSelectionState } from '@tanstack/react-table'
import AddCategoryDialog from './AddCategoryDialog';
import AddMatchDialog from './AddMatchDialog';
import { JsonEditor, githubDarkTheme } from 'json-edit-react'
import { Button, ButtonGroup, ButtonToolbar } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { StatusContext, StatusContextType } from './StatusContext';
import ProbeTable from './ProbeTable';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useLocation } from 'react-router-dom';
import Dropdown from 'react-bootstrap/Dropdown';
import FingerGeoMap from './FingerGeoMap';
import { validLatLon } from '../utils/geoCoords';
// @ts-ignore
import countryCentroidsGeoJson from 'world-countries-centroids/dist/countries.geojson?raw';

const defaultFingerprint: Fingerprint = {
    fid: 0,
    pvkey: "",
    layer: 0,
    product: "",
    product_tag: "",
    vendor: "",
    vendor_tag: "",
    title: "",
    description: "",
    categories: [],
    presentation: {},
    res_type: "",
    res_tags: {},
    res_score: {},
    res_deco: {},
};

interface EditState extends Fingerprint {
    backendSelected?: boolean,
    matches: FingerMatchEntry[],
    probes: any[]
};

const defaultEditState: EditState = {
    ...defaultFingerprint,
    backendSelected: false,
    matches: [],
    probes: []
};

type CountryCodeOption = {
    code: string;
    name: string;
};

type CountryData = {
    options: CountryCodeOption[];
    geoNamesByCode: Record<string, string[]>;
};

const COUNTRY_DATA: CountryData = (() => {
    try {
        const parsed = JSON.parse(countryCentroidsGeoJson);
        const optionsByCode = new Map<string, string>();
        const geoNamesByCode = new Map<string, Set<string>>();

        for (const feature of parsed?.features || []) {
            const code = String(feature?.properties?.ISO || '').trim().toUpperCase();
            const countryName = String(
                feature?.properties?.COUNTRY ||
                feature?.properties?.COUNTRYAFF ||
                feature?.properties?.NAME ||
                feature?.properties?.name ||
                feature?.properties?.ADMIN ||
                feature?.properties?.ISO ||
                ''
            ).trim();
            const affiliatedName = String(feature?.properties?.COUNTRYAFF || '').trim();

            if (!/^[A-Z]{2}$/.test(code)) {
                continue;
            }

            if (countryName) {
                if (!geoNamesByCode.has(code)) {
                    geoNamesByCode.set(code, new Set<string>());
                }
                geoNamesByCode.get(code)?.add(countryName);
                if (affiliatedName && affiliatedName !== countryName) {
                    geoNamesByCode.get(code)?.add(affiliatedName);
                }
            }

            if (countryName && !optionsByCode.has(code)) {
                optionsByCode.set(code, countryName);
            }
        }

        return {
            options: Array.from(optionsByCode.entries())
                .map(([code, name]) => ({ code, name }))
                .sort((left, right) => left.name.localeCompare(right.name) || left.code.localeCompare(right.code)),
            geoNamesByCode: Array.from(geoNamesByCode.entries()).reduce((acc, [code, names]) => {
                acc[code] = Array.from(names.values());
                return acc;
            }, {} as Record<string, string[]>)
        };
    } catch {
        return { options: [], geoNamesByCode: {} };
    }
})();

const COUNTRY_CODE_OPTIONS: CountryCodeOption[] = COUNTRY_DATA.options;
const COUNTRY_GEO_NAMES_BY_CODE: Record<string, string[]> = COUNTRY_DATA.geoNamesByCode;

const COUNTRY_NAME_ALIASES: Record<string, string[]> = {
    'united states': ['united states of america'],
    'united states of america': ['united states'],
    'russian federation': ['russia'],
    'czech republic': ['czechia'],
    'micronesia federated states of': ['micronesia'],
    'korea republic of': ['south korea'],
    'korea democratic peoples republic of': ['north korea']
};

function normalizeCountryName(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

const FQDN_MATCH_FIELDS: Array<'tls_sni' | 'recent_dns' | 'forward_dns'> = ['tls_sni', 'recent_dns', 'forward_dns'];

function normalizeHostCandidate(value: string): string {
    let host = String(value || '').trim().toLowerCase();
    if (!host) {
        return '';
    }
    host = host.replace(/^["'`\s]+|["'`\s]+$/g, '');
    host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    host = host.replace(/^.*@/, '');
    host = host.replace(/[/?#].*$/, '');
    host = host.replace(/^\[|\]$/g, '');
    host = host.replace(/^\*\./, '');
    host = host.replace(/:\d+$/, '');
    host = host.replace(/\.+$/, '');
    host = host.replace(/^[^a-z0-9]+|[^a-z0-9.\-]+$/g, '');
    return host;
}

function isDnsHostname(host: string): boolean {
    if (!host || !host.includes('.') || host.length > 253) {
        return false;
    }
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return false;
    }
    const labels = host.split('.');
    if (labels.length < 2) {
        return false;
    }
    for (const label of labels) {
        if (!label || label.length > 63) {
            return false;
        }
        if (!/^[a-z0-9-]+$/.test(label)) {
            return false;
        }
        if (label.startsWith('-') || label.endsWith('-')) {
            return false;
        }
    }
    const tld = labels[labels.length - 1];
    if (/^\d+$/.test(tld)) {
        return false;
    }
    return true;
}

function isValidBareFqdn(value: string): boolean {
    const raw = String(value || '').trim();
    if (!raw) {
        return false;
    }
    if (/\s/.test(raw) || /[/?#@]/.test(raw)) {
        return false;
    }
    if (raw.includes('://') || raw.includes('\\')) {
        return false;
    }
    if (raw.includes(':')) {
        return false;
    }
    if (/^["'`]|["'`]$/.test(raw)) {
        return false;
    }
    if (raw.startsWith('*.') || raw.endsWith('.')) {
        return false;
    }
    return isDnsHostname(raw.toLowerCase());
}

function getInvalidFqdnFields(ent: FingerMatchEntry): string[] {
    return FQDN_MATCH_FIELDS.filter((field) => {
        const value = (ent as any)?.[field];
        if (value === null || value === undefined || String(value).trim() === '') {
            return false;
        }
        return !isValidBareFqdn(String(value));
    });
}

function buildCountryMatchKeys(names: string[]): Set<string> {
    const keys = new Set<string>();
    for (const name of names) {
        const normalized = normalizeCountryName(name);
        if (!normalized) {
            continue;
        }
        keys.add(normalized);
        const aliases = COUNTRY_NAME_ALIASES[normalized] || [];
        for (const alias of aliases) {
            const normalizedAlias = normalizeCountryName(alias);
            if (normalizedAlias) {
                keys.add(normalizedAlias);
            }
        }
    }
    return keys;
}

interface DuplicateConflictState {
    duplicateId: number;
    message: string;
}

type FingerEditMode = 'legacy' | 'modern';

const FINGER_EDIT_MODE_KEY = '/display/fingerEditMode';
const FINGER_EDIT_MODE_DESC = 'Fingerprint editor mode default (legacy|modern)';

function FingerEdit({ fid }: { fid: number }) {
    const [catData, setCatData] = useState<Category[]>([]);
    const [schemaData, setSchemaData] = useState<any>(null);
    const [refresh, setRefresh] = useState<boolean>(true);
    const [ fdata, setFdata ] = useState<EditState>(defaultEditState);
    const [ origData, setOrigData ] = useState<EditState>(defaultEditState);
    const [ showInsertMatchDialog, setShowInsertMatchDialog ] = useState<boolean>(false);
    const [ editMatchIndex, setEditMatchIndex ] = useState<number | null>(null);
    const [ probes, setProbes ] = useState<any[]>([]);
    const [ duplicateConflict, setDuplicateConflict ] = useState<DuplicateConflictState | null>(null);
    const [ editorMode, setEditorMode ] = useState<FingerEditMode>('legacy');
    const [ modeLoaded, setModeLoaded ] = useState<boolean>(false);
    const [ showGeoModal, setShowGeoModal ] = useState<boolean>(false);
    const [ intermediateHostInput, setIntermediateHostInput ] = useState<string>('');
    const [ intermediateHostApi, setIntermediateHostApi ] = useState<boolean>(false);
    const [ idCopyAnimating, setIdCopyAnimating ] = useState<boolean>(false);
    const [ pvkeyCopyAnimating, setPvkeyCopyAnimating ] = useState<boolean>(false);
    const [ countryCodeFilter, setCountryCodeFilter ] = useState<string>('');
    const [ showCountryCodeDropdown, setShowCountryCodeDropdown ] = useState<boolean>(false);
    const [ countryCodeDropDirection, setCountryCodeDropDirection ] = useState<'up' | 'down'>('down');
    const [ countryCodeDropAlign, setCountryCodeDropAlign ] = useState<'start' | 'end'>('start');
    // probe table stuff
    const [ probeSelection, setProbeSelection ] = useState<RowSelectionState>({});
    const countryCodeToggleRef = useRef<HTMLElement | null>(null);
    const countryCodeFilterRef = useRef<HTMLInputElement | null>(null);
    const idCopyAnimTimeoutRef = useRef<number | null>(null);
    const pvkeyCopyAnimTimeoutRef = useRef<number | null>(null);

    const statusContext = useContext(StatusContext);
    const navigate = useNavigate();
    const theme : any = useTheme();

    const location = useLocation();
    const returnUrl = (location.state as any)?.returnUrl;
    useEffect(() => {
        async function loadEditorMode() {
            try {
                const rc = await getSetting(FINGER_EDIT_MODE_KEY).catch(() => 'legacy');
                setEditorMode(rc === 'modern' ? 'modern' : 'legacy');
            } catch (error: any) {
                console.error('Error loading finger editor mode:', error);
                setEditorMode('legacy');
            } finally {
                setModeLoaded(true);
            }
        }
        loadEditorMode();
    }, []);

    useEffect(() => {
        async function fetchFingerprint() {
            if (!refresh) {
                return;
            }
            try {
                const response = await getFingerprint(fid);
                const matches = await getMatchesForFinger(fid);
                const probes = await getProbesForFinger(fid);
                const estate: EditState = {
                    ...response,
                    categories: Array.isArray(response?.categories) ? response.categories : [],
                    matches: Array.isArray(matches) ? matches : [],
                    probes: Array.isArray(probes) ? probes : []
                };
                setDuplicateConflict(null);
                setFdata(estate);
                setOrigData(estate); // keep the original data for comparison
                const mprobes = await getProbesForFinger(fid);
                setProbes(Array.isArray(mprobes) ? mprobes : []);

                const catResponse = await getCategories();
                setCatData(catResponse); // set the categories for the fingerprint info
                const sd = await getSchemas();
                setSchemaData(sd);
            } catch (error : any) {
                statusContext.postError(`Error fetching fid ${fid} : ${error.message}`);
                console.error(`Error fetching fid ${fid}:`, error);
            }
            setRefresh(false);
        }
        fetchFingerprint();
    }, [ refresh, fid ]);

    useEffect(() => {
        if (showCountryCodeDropdown) {
            window.requestAnimationFrame(() => {
                countryCodeFilterRef.current?.focus({ preventScroll: true });
            });
        }
    }, [ showCountryCodeDropdown, countryCodeDropDirection ]);

    function matchText(ent: FingerMatchEntry) {
        let out = [] as string[];
        if (ent.ip) {
            out.push('ip=' + ent.ip);
        }
        if (ent.reverse_dns) {
            out.push('rdns=' + ent.reverse_dns);
        }
        if (ent.forward_dns) {
            out.push('fdns=' + ent.forward_dns);
        }
        if (ent.recent_dns) {
            out.push('recent_dns=' + ent.recent_dns);
        }
        if (ent.tls_cert_subj) {
            out.push('tls_cert_subj=' + ent.tls_cert_subj);
        }
        if (ent.tls_sni) {
            out.push('tls_sni=' + ent.tls_sni);
        }
        if (ent.tls_alpn) {
            out.push('tls_alpn=' + ent.tls_alpn);
        }
        return out.join(' && ');
    }
    function categoryName(ent: Category): string {
        const entryKey = String(ent as unknown);
        const ce = catData.find((c) => c.key === entryKey);
        return ce?.title || ce?.key || entryKey;
    }

    function categoryHoverText(ent: Category): string {
        const entryKey = String(ent as unknown);
        const ce = catData.find((c) => c.key === entryKey);
        if (!ce) {
            return entryKey;
        }
        const details = [ce.title || ce.key, ce.description].filter((v) => String(v || '').trim().length > 0);
        return details.join(' - ');
    }
    function setPresentationData(data: any) {
        setFdata({ ...fdata, presentation: data });
    }

    function setResTagsData(data: any) {
        setFdata({ ...fdata, res_tags: data });
    }

    function updateResTagField(key: 'address' | 'country' | 'country_code' | 'notes' | 'latitude' | 'longitude', value: string) {
        const nextTags = {
            ...((fdata.res_tags && typeof fdata.res_tags === 'object') ? (fdata.res_tags as Record<string, any>) : {})
        };

        if (value.trim() === '') {
            delete nextTags[key];
        } else {
            nextTags[key] = value;
        }

        setResTagsData(nextTags);
    }

    function clearResTagField(key: string) {
        const nextTags = {
            ...((fdata.res_tags && typeof fdata.res_tags === 'object') ? (fdata.res_tags as Record<string, any>) : {})
        };

        delete nextTags[key];
        setResTagsData(nextTags);
    }

    function toBooleanTag(value: any): boolean {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'number') {
            return value !== 0;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
        }
        return false;
    }

    function updateResTagBooleanField(key: 'yeet_pending', value: boolean) {
        const nextTags = {
            ...((fdata.res_tags && typeof fdata.res_tags === 'object') ? (fdata.res_tags as Record<string, any>) : {})
        };

        nextTags[key] = value;
        setResTagsData(nextTags);
    }

    function normalizeIntermediateHostname(value: string): string {
        let host = value.trim().toLowerCase();
        if (!host) {
            return '';
        }
        host = host.replace(/^[a-z]+:\/\//, '');
        host = host.split('/')[0] || host;
        host = host.replace(/:\d+$/, '');
        host = host.replace(/\.+$/, '');
        return host;
    }

    function rewriteIntermediateDnsNames(
        dnsNames: Record<string, any>,
        host: string,
        options: { remove?: boolean; api?: boolean } = {}
    ) {
        const normalizedHost = normalizeIntermediateHostname(host);
        if (!normalizedHost) {
            return { dnsNames, changed: false };
        }

        const nextDnsNames: Record<string, any> = {};
        let existingPayload: Record<string, any> = {};
        let hadMatch = false;

        for (const [key, rawValue] of Object.entries(dnsNames)) {
            const keyNormalized = normalizeIntermediateHostname(key);
            if (keyNormalized !== normalizedHost) {
                nextDnsNames[key] = rawValue;
                continue;
            }

            hadMatch = true;
            if (rawValue && typeof rawValue === 'object') {
                existingPayload = { ...existingPayload, ...(rawValue as Record<string, any>) };
            }
        }

        if (options.remove) {
            return { dnsNames: nextDnsNames, changed: hadMatch };
        }

        nextDnsNames[normalizedHost] = {
            ...existingPayload,
            api: !!options.api
        };

        const wasCanonical = hadMatch
            && Object.keys(dnsNames).length === Object.keys(nextDnsNames).length
            && Object.prototype.hasOwnProperty.call(dnsNames, normalizedHost)
            && toBooleanTag((dnsNames[normalizedHost] && typeof dnsNames[normalizedHost] === 'object')
                ? (dnsNames[normalizedHost] as Record<string, any>).api
                : false) === !!options.api;

        return { dnsNames: nextDnsNames, changed: !wasCanonical || !hadMatch };
    }

    function setIntermediateHostApiFlag(host: string, api: boolean) {
        const nextTags = {
            ...((fdata.res_tags && typeof fdata.res_tags === 'object') ? (fdata.res_tags as Record<string, any>) : {})
        };
        const dnsNames = (nextTags.dns_names && typeof nextTags.dns_names === 'object')
            ? { ...(nextTags.dns_names as Record<string, any>) }
            : {};
        const { dnsNames: nextDnsNames, changed } = rewriteIntermediateDnsNames(dnsNames, host, { api });
        if (!changed) {
            return;
        }
        nextTags.dns_names = nextDnsNames;
        setResTagsData(nextTags);
    }

    function removeIntermediateHost(host: string) {
        const nextTags = {
            ...((fdata.res_tags && typeof fdata.res_tags === 'object') ? (fdata.res_tags as Record<string, any>) : {})
        };
        const dnsNames = (nextTags.dns_names && typeof nextTags.dns_names === 'object')
            ? { ...(nextTags.dns_names as Record<string, any>) }
            : {};
        const { dnsNames: nextDnsNames, changed } = rewriteIntermediateDnsNames(dnsNames, host, { remove: true });
        if (!changed) {
            return;
        }
        if (Object.keys(nextDnsNames).length === 0) {
            delete nextTags.dns_names;
        } else {
            nextTags.dns_names = nextDnsNames;
        }
        setResTagsData(nextTags);
    }

    function addIntermediateHost() {
        const normalizedHost = normalizeIntermediateHostname(intermediateHostInput);
        if (!normalizedHost) {
            return;
        }
        setIntermediateHostApiFlag(normalizedHost, intermediateHostApi);
        setIntermediateHostInput('');
        setIntermediateHostApi(false);
    }

    function formatLastUpdateLabel(key: string) {
        return key
            .replace(/^last_/, '')
            .replace(/_update$/, '')
            .split('_')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    function getDaysSince(timestamp: any) {
        if (typeof timestamp !== 'string' || timestamp.trim() === '') {
            return null;
        }

        const parsed = new Date(timestamp);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }

        return Math.max(0, Math.floor((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24)));
    }

    function setCountryCode(code: string) {
        updateResTagField('country_code', code.trim().toUpperCase());
        setCountryCodeFilter('');
        setShowCountryCodeDropdown(false);
    }

    function handleCountryCodeToggleClick() {
        if (showCountryCodeDropdown) {
            setShowCountryCodeDropdown(false);
            setCountryCodeFilter('');
            return;
        }

        const rect = countryCodeToggleRef.current?.getBoundingClientRect();
        const menuHeight = 320;
        const menuWidth = 320;
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const spaceRight = window.innerWidth - rect.left;
            const spaceLeft = rect.right;
            setCountryCodeDropDirection(spaceBelow < menuHeight && spaceAbove > spaceBelow ? 'up' : 'down');
            setCountryCodeDropAlign(spaceRight >= menuWidth || spaceRight >= spaceLeft ? 'start' : 'end');
        }

        setCountryCodeFilter(String(resTags.country_code || '').toUpperCase());
        setShowCountryCodeDropdown(true);
    }

    function handleCountryCodeListWheel(event: React.WheelEvent<HTMLDivElement>) {
        const element = event.currentTarget;
        const scrollingUp = event.deltaY < 0;
        const scrollingDown = event.deltaY > 0;
        const atTop = element.scrollTop <= 0;
        const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

        if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    async function switchEditorMode(mode: FingerEditMode) {
        setEditorMode(mode);
        try {
            await setSetting(FINGER_EDIT_MODE_KEY, mode, FINGER_EDIT_MODE_DESC);
        } catch (error: any) {
            statusContext.postError(`Unable to persist editor mode: ${error.message}`);
        }
    }

    async function doSave() {
        if (!fdata) {
            return;
        }
        try {
            await setFingerprint(fdata);
            await setMatchesForFinger(fdata.fid, fdata.matches);
            setDuplicateConflict(null);
            setRefresh(true);
        } catch (error : any) {
            if (error instanceof WsRpcError && error.rpcinfo?.code === 'DUPLICATE_PVKEY_LAYER') {
                const duplicateId = Number(error.rpcinfo?.duplicateId);
                if (Number.isFinite(duplicateId)) {
                    setDuplicateConflict({
                        duplicateId,
                        message: `Duplicate fingerprint identity. ID ${duplicateId} already has pvkey/layer ${fdata.pvkey}/${fdata.layer}.`
                    });
                } else {
                    setDuplicateConflict({
                        duplicateId: 0,
                        message: 'Duplicate fingerprint identity. Another fingerprint already uses this pvkey/layer.'
                    });
                }
            } else if (error instanceof WsRpcError && error.rpcinfo?.code === 'PAYLOAD_TOO_LARGE') {
                const byteSize = error.rpcinfo?.byteSize;
                const limit = error.rpcinfo?.limit;
                const sizeMB = byteSize ? (byteSize / (1024 * 1024)).toFixed(2) : '?';
                statusContext.postError(
                    `Fingerprint data (${sizeMB} MB) exceeds 8 MB limit. ` +
                    `Try reducing the size of Tags or Presentation fields by removing unnecessary content.`
                );
            } else {
                statusContext.postError(`Error saving fingerprint ${fid}: ${error.message}`);
            }
            console.error(`Error saving fingerprint ${fid}:`, error);
        }
        // navigate(-1);       // go back to fingerprints table
    }
    async function doDuplicate() {
        if (!fdata) {
            return;
        }
        try {
            let id = await createFingerprint();
            let newFingerprint = { ...fdata, fid: id, 
                                   pvkey: fdata.pvkey + '__copy' + Math.floor(Math.random() * 1000),
             } as EditState;
            await setFingerprint(newFingerprint);
            setRefresh(true);
            navigate(`/fingerprint/${id}`);
        } catch (error : any) {
            statusContext.postError(`Error duplicating fingerprint ${fid}: ${error.message}`);
            console.error('Error duplicating fingerprint ${key}:', error);
        }
    }
    async function doDelete() {
        if (!fdata) {
            return;
        }
        try {
            await deleteFingerprint(fid);
//            setFSelection({});
            if (returnUrl) {
                navigate(returnUrl);
            } else {
                navigate(-1);
            }
        } catch (error : any) {
            statusContext.postError(`Error deleting fingerprint ${fid}: ${error.message}`);
            console.error('Error deleting fingerprint ${key}:', error);
        }
    }

    function changeWorkingSet(checked: boolean) {
        setFdata({ ...fdata,
                   backendSelected: checked
        });
    }

    async function doPvupdate(full : boolean) {
        const probeId = await queueAiProbe('update_pv', {
            parent_id: 0,
            desc: `Update FP ${fid}`,
            finger_id: fid,
            args: {
                ai_desc: full,
                ai_geo: full,
                ai_api: full,
                ai_categories: full,
            }
        });
        statusContext.postInfo(`Requested PV update for fid ${fid}, probeId=${probeId}`);
        setTimeout(() => {
            execControl(`run ${probeId}`);
        }, 2000);
        navigate('/dashboard/exec-status');
    }

    function doRevert() {
        setFdata(origData);
    }

    function doCancel() {
        if (returnUrl) {
            navigate(returnUrl);
        } else {
            navigate(-1);
        }
    }

    function openAddMatchDialog() {
        setEditMatchIndex(null);
        setShowInsertMatchDialog(true);
    }

    function openEditMatchDialog(index: number) {
        setEditMatchIndex(index);
        setShowInsertMatchDialog(true);
    }

    function triggerCopyButtonAnimation(target: 'id' | 'pvkey') {
        if (target === 'id') {
            setIdCopyAnimating(false);
            window.requestAnimationFrame(() => setIdCopyAnimating(true));
            if (idCopyAnimTimeoutRef.current !== null) {
                window.clearTimeout(idCopyAnimTimeoutRef.current);
            }
            idCopyAnimTimeoutRef.current = window.setTimeout(() => {
                setIdCopyAnimating(false);
                idCopyAnimTimeoutRef.current = null;
            }, 240);
            return;
        }

        setPvkeyCopyAnimating(false);
        window.requestAnimationFrame(() => setPvkeyCopyAnimating(true));
        if (pvkeyCopyAnimTimeoutRef.current !== null) {
            window.clearTimeout(pvkeyCopyAnimTimeoutRef.current);
        }
        pvkeyCopyAnimTimeoutRef.current = window.setTimeout(() => {
            setPvkeyCopyAnimating(false);
            pvkeyCopyAnimTimeoutRef.current = null;
        }, 240);
    }

    useEffect(() => {
        return () => {
            if (idCopyAnimTimeoutRef.current !== null) {
                window.clearTimeout(idCopyAnimTimeoutRef.current);
            }
            if (pvkeyCopyAnimTimeoutRef.current !== null) {
                window.clearTimeout(pvkeyCopyAnimTimeoutRef.current);
            }
        };
    }, []);

    async function copyPvkey() {
        try {
            triggerCopyButtonAnimation('pvkey');
            if (!fdata?.pvkey) {
                return;
            }
            await navigator.clipboard.writeText(fdata.pvkey);
            statusContext.postInfo('PvKey copied to clipboard');
        } catch (error: any) {
            statusContext.postError(`Failed to copy PvKey: ${error?.message || error}`);
        }
    }

    async function copyFingerprintId() {
        try {
            triggerCopyButtonAnimation('id');
            if (!Number.isFinite(Number(fdata?.fid))) {
                return;
            }
            await navigator.clipboard.writeText(String(fdata.fid));
            statusContext.postInfo('Fingerprint ID copied to clipboard');
        } catch (error: any) {
            statusContext.postError(`Failed to copy fingerprint ID: ${error?.message || error}`);
        }
    }

    if (!schemaData) {
        return (
            <div className="alert alert-danger">
                Loading...
            </div>
        )
    }

    /*
    const ajv = new Ajv();
    const presValidate = ajv.compile(schemaData.finger.properties.presentation);
    const tagsValidate = ajv.compile(schemaData.finger.properties.results.properties.tags);
    */

    const saveable = isEqual(origData, fdata) === false;
    const duplicateActive = duplicateConflict !== null;
    const duplicateBorderColor = theme.colors.danger || '#dc3545';
    const duplicateTextColor = theme.colors.danger || '#dc3545';
    const duplicateBgColor = theme.colors.bg2 || theme.colors.headerBg || theme.colors.bg || '#343a40';
    const duplicateFieldStyle = duplicateActive ? {
        borderColor: duplicateBorderColor,
        backgroundColor: duplicateBgColor,
        color: theme.colors.text || '#fff'
    } : undefined;

    const modernCardStyle: React.CSSProperties = {
        background: theme.colors.headerBg || '#171a21',
        border: `1px solid ${theme.colors.border || '#2a3140'}`,
        borderRadius: 8,
        padding: '0.9rem 1rem',
        marginBottom: '0.85rem'
    };

    const modernInputStyle: React.CSSProperties = {
        backgroundColor: theme.colors.bg || '#111318',
        color: theme.colors.text || '#e6eaf2',
        border: `1px solid ${theme.colors.border || '#2a3140'}`
    };

    const sharedJsonEditorProps = {
        collapse: 1,
        rootFontSize: '13px',
        theme: githubDarkTheme
    } as const;

    const resTags = (fdata.res_tags && typeof fdata.res_tags === 'object')
        ? (fdata.res_tags as Record<string, any>)
        : {};
    const currentCountryCode = String(resTags.country_code || '').toUpperCase();
    const countryCodeFilterLower = countryCodeFilter.trim().toLowerCase();
    const filteredCountryCodeOptions = COUNTRY_CODE_OPTIONS
        .filter((option) => {
            if (countryCodeFilterLower === '') {
                return true;
            }

            if (countryCodeFilterLower.length === 1) {
                return option.code.toLowerCase().startsWith(countryCodeFilterLower) || option.name.toLowerCase().startsWith(countryCodeFilterLower);
            }

            return option.code.toLowerCase().includes(countryCodeFilterLower) || option.name.toLowerCase().includes(countryCodeFilterLower);
        })
        .sort((left, right) => {
            if (countryCodeFilterLower === '') {
                return left.name.localeCompare(right.name) || left.code.localeCompare(right.code);
            }

            const leftStarts = left.code.toLowerCase().startsWith(countryCodeFilterLower) || left.name.toLowerCase().startsWith(countryCodeFilterLower);
            const rightStarts = right.code.toLowerCase().startsWith(countryCodeFilterLower) || right.name.toLowerCase().startsWith(countryCodeFilterLower);

            if (leftStarts !== rightStarts) {
                return leftStarts ? -1 : 1;
            }

            return left.name.localeCompare(right.name) || left.code.localeCompare(right.code);
        });
    const selectedCountryCodeOption = COUNTRY_CODE_OPTIONS.find((option) => option.code === currentCountryCode) || null;
    const customCountryCode = countryCodeFilter.trim().toUpperCase();
    const showCustomCountryCodeOption = customCountryCode.length === 2 && !COUNTRY_CODE_OPTIONS.some((option) => option.code === customCountryCode);
    const geoCoords = validLatLon(resTags.latitude ?? resTags?.geo?.lat,
                                  resTags.longitude ?? resTags?.geo?.lon);
    const geoLat = geoCoords?.lat ?? null;
    const geoLon = geoCoords?.lon ?? null;
    const hasGeoCoordinates = geoCoords !== null;
    const geoCountryCode = String(resTags.country_code || '').trim().toUpperCase();
    const geoCountryNamesFromCode = COUNTRY_GEO_NAMES_BY_CODE[geoCountryCode] || [];
    const geoCountryName = String(resTags.country || '').trim();
    const geoCountryMatchKeys = buildCountryMatchKeys([
        ...geoCountryNamesFromCode,
        selectedCountryCodeOption?.name || '',
        geoCountryName
    ]);
    const geoIsCountryMatch = (geoName: string) => geoCountryMatchKeys.has(normalizeCountryName(geoName));
    const canOpenGeoMap = hasGeoCoordinates || geoCountryMatchKeys.size > 0 || geoCountryCode.length === 2;
    const intermediateDnsNames = (resTags.dns_names && typeof resTags.dns_names === 'object')
        ? (resTags.dns_names as Record<string, any>)
        : {};
    const intermediateHostEntries: Array<{ host: string; api: boolean }> = Array.from(Object.entries(intermediateDnsNames)
        .reduce((acc, [host, payload]) => {
            const normalizedHost = normalizeIntermediateHostname(host);
            if (!normalizedHost) {
                return acc;
            }

            const api = toBooleanTag((payload && typeof payload === 'object') ? (payload as Record<string, any>).api : false);
            const existing = acc.get(normalizedHost);
            acc.set(normalizedHost, {
                host: normalizedHost,
                api: existing ? (existing.api || api) : api
            });
            return acc;
        }, new Map<string, { host: string; api: boolean }>())
        .values())
        .sort((left, right) => left.host.localeCompare(right.host));
    const hasYeetPending = Object.prototype.hasOwnProperty.call(resTags, 'yeet_pending');
    const yeetPending = toBooleanTag((resTags as Record<string, any>).yeet_pending);
    const yeetPendingStateLabel = hasYeetPending ? (yeetPending ? 'true' : 'false') : '--';
    const lastUpdateEntries = Object.entries(resTags)
        .filter(([key, value]) => key.startsWith('last_') && key.endsWith('_update') && typeof value === 'string')
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));

    const topActionLabel = 'Try new editor';
    const topActionIcon = faSquarePlus;
    const fingerActions = [
        {
            key: 'delete',
            label: 'Delete',
            menuLabel: 'Delete Fingerprint',
            icon: faTrashCan,
            variant: 'outline-danger',
            help: 'Permanently delete this fingerprint and return to the fingerprints list.',
            onClick: () => doDelete()
        },
        {
            key: 'dup',
            label: 'Clone',
            menuLabel: 'Clone Fingerprint',
            icon: faClone,
            variant: 'outline-primary',
            help: 'Create a duplicate fingerprint with a generated copy pvkey and open the clone.',
            onClick: () => doDuplicate()
        },
        {
            key: 'pvupdate2',
            label: 'Full Update',
            menuLabel: 'Full Update',
            icon: faPencil,
            variant: 'outline-warning',
            help: 'Queue the full AI fingerprint update workflow and open execution status tracking.',
            onClick: () => doPvupdate(true)
        },
        {
            key: 'pvupdate',
            label: 'Quick Review',
            menuLabel: 'Quick Review',
            icon: faPencil,
            variant: 'outline-info',
            help: 'Queue a validation-oriented AI update that reviews description, geo, API, and categories without the full rewrite path.',
            onClick: () => doPvupdate(false)
        },
    ] as const;

    function duplicateAlert() {
        if (!duplicateConflict) {
            return null;
        }
        return (
            <div className="mb-3 p-2 rounded" style={{
                backgroundColor: duplicateBgColor,
                border: `1px solid ${duplicateBorderColor}`
            }}>
                <div className="fw-semibold" style={{ color: duplicateTextColor }}>{duplicateConflict.message}</div>
                <div className="d-flex align-items-center gap-2">
                    <Button
                        variant="link"
                        type="button"
                        className="p-0"
                        disabled={duplicateConflict.duplicateId <= 0}
                        onClick={() => {
                            if (duplicateConflict.duplicateId > 0) {
                                window.open(`/fingerprint/${duplicateConflict.duplicateId}`, '_blank', 'noopener,noreferrer');
                            }
                        }}
                    >
                        Open fingerprint {duplicateConflict.duplicateId}
                    </Button>
                    <span style={{ color: theme.colors.textMuted || '#888', backgroundColor: duplicateBgColor }}>Opens in a new tab; current edits are preserved here.</span>
                </div>
            </div>
        );
    }

    function renderLegacyDetailSections() {
        return (
            <>
                <Row>
                    <Col>
                        <Row>
                            <h5 style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                <span className="text-decoration-underline">Categories</span>
                                <AddCategoryDialog editFp={fdata} setEditFp={setFdata} catData={catData} />
                            </h5>
                            <BTable hover responsive size="sm">
                                <tbody>
                                    {fdata.categories.map((ent, i) =>
                                        <tr key={i}>
                                            <td title={categoryHoverText(ent)}>{categoryName(ent)}</td>
                                            <td style={{ width: '1px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                <Button variant="outline-danger" size="sm"
                                                    style={{ padding: '0.1rem 0.3rem', lineHeight: 1 }}
                                                    onClick={() => {
                                                        const newCategories = [...fdata.categories];
                                                        newCategories.splice(i, 1);
                                                        setFdata({ ...fdata, categories: newCategories });
                                                    }}>
                                                    <FontAwesomeIcon icon={faTrashCan} />
                                                </Button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </BTable>
                        </Row>
                        <Row className="mt-4">
                            <h5 style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                <span className="text-decoration-underline">Matches</span>
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    className="ms-2"
                                    style={{ display: 'inline-flex', alignItems: 'center', paddingTop: '0.1rem', paddingBottom: '0.1rem', lineHeight: 1, textDecoration: 'none' }}
                                    onClick={() => openAddMatchDialog()}
                                >
                                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Add</span>
                                </Button>
                            </h5>
                            <BTable hover responsive size="sm">
                                <tbody>
                                    {fdata.matches.map((ent, i) => {
                                        const invalidFields = getInvalidFqdnFields(ent);
                                        return (
                                            <tr key={i}>
                                                <td
                                                    role="button"
                                                    tabIndex={0}
                                                    title="Edit match"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => openEditMatchDialog(i)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            openEditMatchDialog(i);
                                                        }
                                                    }}
                                                >
                                                    {matchText(ent)}
                                                </td>
                                                <td style={{ width: '1px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                    {invalidFields.length > 0 && (
                                                        <Button
                                                            variant="outline-warning"
                                                            size="sm"
                                                            title={`Invalid fqdn fields: ${invalidFields.join(', ')}`}
                                                            style={{ padding: '0.1rem 0.3rem', lineHeight: 1, marginRight: '0.25rem' }}
                                                        >
                                                            <FontAwesomeIcon icon={faSkullCrossbones} />
                                                        </Button>
                                                    )}
                                                    <Button variant="outline-danger" size="sm"
                                                        style={{ padding: '0.1rem 0.3rem', lineHeight: 1 }}
                                                        onClick={() => {
                                                            const newMatches = [...fdata.matches];
                                                            newMatches.splice(i, 1);
                                                            setFdata({ ...fdata, matches: newMatches });
                                                        }}>
                                                        <FontAwesomeIcon icon={faTrashCan} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </BTable>
                        </Row>
                    </Col>
                    <Col>
                        <Form.Group as={Row} className="mb-3" controlId="f_tags"
                            style={{ minHeight: '15em', maxHeight: '52em', overflow: 'auto' }}>
                            <Form.Label>Tags</Form.Label>
                            <JsonEditor
                                data={fdata.res_tags}
                                collapse={1}
                                rootName="res_tags"
                                rootFontSize="13px"
                                theme={githubDarkTheme}
                                onUpdate={({ newData }) => {
                                    setFdata({ ...fdata, res_tags: newData as any })
                                }}
                            />
                            <Form.Text className="text-muted">
                                These tags are used by the FBOT and customer policy engine.
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <h5 className="text-decoration-underline">Probes</h5>
                    <ProbeTable rowSelection={probeSelection} setRowSelection={setProbeSelection} data={probes} doSearch={false} />
                </Row>
                <Row>
                    <Form.Group as={Col} className="mb-3" controlId="f_presentation">
                        <Form.Label>Presentation Overrides</Form.Label>
                        <JsonEditor
                            {...sharedJsonEditorProps}
                            data={fdata.presentation}
                            rootName="presentation"
                            onUpdate={({ newData }) => {
                                setPresentationData(newData as any)
                            }}
                        />
                        <Form.Text className="text-muted">
                            Presentation overrides for the fingerprint. This JSON object supports theme overrides.
                        </Form.Text>
                    </Form.Group>
                </Row>
            </>
        );
    }

    function renderModernCategoryAndMatchSection() {
        return (
            <Row>
                <Col lg={6}>
                    <div style={modernCardStyle}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">Category Mapping</h5>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge text-bg-secondary">{fdata.categories.length}</span>
                                <AddCategoryDialog editFp={fdata} setEditFp={setFdata} catData={catData} />
                            </div>
                        </div>
                        <div className="text-muted small mb-3">Map this fingerprint to product taxonomy categories.</div>
                        {fdata.categories.length === 0 ? (
                            <div className="text-muted small">No categories assigned yet.</div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {fdata.categories.map((ent, i) => (
                                    <div
                                        key={i}
                                        className="d-flex align-items-center justify-content-between gap-2 rounded"
                                        style={{
                                            backgroundColor: theme.colors.bg || '#111318',
                                            border: `1px solid ${theme.colors.border || '#2a3140'}`,
                                            padding: '0.45rem 0.55rem'
                                        }}
                                    >
                                        <div className="min-w-0" title={categoryHoverText(ent)}>
                                            <span className="fw-semibold text-truncate d-inline-block" style={{ maxWidth: '100%' }}>{categoryName(ent)}</span>
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            style={{ padding: '0.1rem 0.3rem', lineHeight: 1, flexShrink: 0 }}
                                            onClick={() => {
                                                const newCategories = [...fdata.categories];
                                                newCategories.splice(i, 1);
                                                setFdata({ ...fdata, categories: newCategories });
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Col>
                <Col lg={6}>
                    <div style={modernCardStyle}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">Match Rules</h5>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge text-bg-secondary">{fdata.matches.length}</span>
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
                                    onClick={() => openAddMatchDialog()}
                                >
                                    Add Match
                                </Button>
                            </div>
                        </div>
                        <div className="text-muted small mb-3">Define detection rules used to identify this fingerprint.</div>
                        {fdata.matches.length === 0 ? (
                            <div className="text-muted small">No match rules configured yet.</div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {fdata.matches.map((ent, i) => {
                                    const invalidFields = getInvalidFqdnFields(ent);
                                    return (
                                        <div
                                            key={i}
                                            className="d-flex align-items-start justify-content-between gap-2 rounded"
                                            style={{
                                                backgroundColor: theme.colors.bg || '#111318',
                                                border: `1px solid ${theme.colors.border || '#2a3140'}`,
                                                padding: '0.45rem 0.55rem'
                                            }}
                                        >
                                            <div
                                                className="font-monospace"
                                                title="Edit match"
                                                style={{ fontSize: '0.8rem', wordBreak: 'break-word', cursor: 'pointer' }}
                                                onClick={() => openEditMatchDialog(i)}
                                            >
                                                {matchText(ent)}
                                            </div>
                                            <div className="d-flex align-items-start gap-1" style={{ flexShrink: 0 }}>
                                                {invalidFields.length > 0 && (
                                                    <Button
                                                        variant="outline-warning"
                                                        size="sm"
                                                        title={`Invalid fqdn fields: ${invalidFields.join(', ')}`}
                                                        style={{ padding: '0.1rem 0.3rem', lineHeight: 1 }}
                                                    >
                                                        <FontAwesomeIcon icon={faSkullCrossbones} />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    style={{ padding: '0.1rem 0.3rem', lineHeight: 1, flexShrink: 0 }}
                                                    onClick={() => {
                                                        const newMatches = [...fdata.matches];
                                                        newMatches.splice(i, 1);
                                                        setFdata({ ...fdata, matches: newMatches });
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        );
    }

    function renderLegacyEditor() {
        return (
            <Row>
                <Col style={{ overflowY: 'auto', height: 'calc(100vh - 48px)' }} className="border-end">
                    <Form
                        className="p-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_pvkey">
                                <Form.Label>PvKey</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="PV Key"
                                    value={fdata?.pvkey}
                                    readOnly
                                    disabled
                                    className="font-monospace"
                                    style={duplicateFieldStyle}
                                />
                                <Form.Text style={duplicateActive ? { color: duplicateTextColor } : undefined} className={duplicateActive ? '' : 'text-muted'}>
                                    {duplicateActive ? 'Duplicate pvkey/layer conflict detected.' : 'This is the content based unique identifier for the fingerprint.'}
                                </Form.Text>
                            </Form.Group>
                            <Form.Group as={Col} className="mb-3" controlId="f_id">
                                <Form.Label>Fingerprint ID</Form.Label>
                                <Form.Control type="text" placeholder="Fingerprint ID" value={fdata?.fid} readOnly disabled />
                                <Form.Text className="text-muted">
                                    This is the unique identifier for the fingerprint in the local database.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group as={Col} className="mb-3" controlId="f_layer">
                                <Form.Label>Layer</Form.Label>
                                <Form.Control type="text" placeholder="Layer" value={fdata?.layer} readOnly disabled />
                                <Form.Text className="text-muted">Layer fingerprint is on.</Form.Text>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_product_tag">
                                <Form.Label>Product Tag</Form.Label>
                                <Form.Control
                                    className="font-monospace"
                                    type="text"
                                    placeholder="Product Tag"
                                    value={fdata?.product_tag || ''}
                                    isInvalid={duplicateActive}
                                    style={duplicateFieldStyle}
                                    onChange={(e) => {
                                        setDuplicateConflict(null);
                                        setFdata({
                                            ...fdata,
                                            pvkey: fdata.vendor_tag + '__' + e.target.value,
                                            product_tag: e.target.value
                                        });
                                    }}
                                />
                                <Form.Text style={duplicateActive ? { color: duplicateTextColor } : undefined} className={duplicateActive ? '' : 'text-muted'}>
                                    {duplicateActive ? 'Duplicate product tag/pvkey combination.' : 'Tag associated with the product.'}
                                </Form.Text>
                            </Form.Group>
                            <Form.Group as={Col} className="mb-3" controlId="f_product">
                                <Form.Label>Product</Form.Label>
                                <Form.Control style={{ fontSize: '1.5rem' }} type="text" placeholder="Product" value={fdata?.product || ''} onChange={(e) => setFdata({ ...fdata, product: e.target.value })} />
                                <Form.Text className="text-muted">Human readable name of the product.</Form.Text>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_vendor_tag">
                                <Form.Label>Vendor Tag</Form.Label>
                                <Form.Control
                                    className="font-monospace"
                                    type="text"
                                    placeholder="Vendor Tag"
                                    value={fdata?.vendor_tag || ''}
                                    isInvalid={duplicateActive}
                                    style={duplicateFieldStyle}
                                    onChange={(e) => {
                                        setDuplicateConflict(null);
                                        setFdata({
                                            ...fdata,
                                            pvkey: e.target.value + '__' + fdata.product_tag,
                                            vendor_tag: e.target.value
                                        });
                                    }}
                                />
                                <Form.Text style={duplicateActive ? { color: duplicateTextColor } : undefined} className={duplicateActive ? '' : 'text-muted'}>
                                    {duplicateActive ? 'Duplicate vendor tag/pvkey combination.' : 'Tag associated with the vendor.'}
                                </Form.Text>
                            </Form.Group>
                            <Form.Group as={Col} className="mb-3" controlId="f_vendor">
                                <Form.Label>Vendor</Form.Label>
                                <Form.Control style={{ fontSize: '1.5rem' }} type="text" placeholder="Vendor" value={fdata?.vendor || ''} onChange={(e) => setFdata({ ...fdata, vendor: e.target.value })} />
                                <Form.Text className="text-muted">Human readable name of the vendor.</Form.Text>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_title">
                                <Form.Label>Title</Form.Label>
                                <Form.Control type="text" placeholder="Enter title" value={fdata?.title || ''} onChange={(e) => setFdata({ ...fdata, title: e.target.value })} />
                                <Form.Text className="text-muted">Brief one line title for the fingerprint.</Form.Text>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="f_description">
                                <Form.Label>Description</Form.Label>
                                <Form.Control as="textarea" rows={3} placeholder="Enter long description" value={fdata?.description || ''} onChange={(e) => setFdata({ ...fdata, description: e.target.value })} />
                                <Form.Text className="text-muted">Provide a detailed description for the fingerprint.</Form.Text>
                            </Form.Group>
                        </Row>
                        {duplicateAlert()}
                        {renderLegacyDetailSections()}
                    </Form>
                </Col>
            </Row>
        );
    }

    const modernScrollColumnStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehaviorY: 'contain',
        scrollbarGutter: 'stable'
    };

    const modernStickyCardStyle: React.CSSProperties = {
        ...modernCardStyle,
        position: 'sticky',
        top: 0,
        zIndex: 2
    };

    function renderModernEditor() {
        return (
            <Row className="gx-0" style={{ height: 'calc(100vh - 48px)', overflow: 'hidden' }}>
                <Col md={8} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
                    <Form
                        className="pt-2 pt-md-3 pb-2 pb-md-3 ps-2 ps-md-3 pe-2 pe-md-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                        style={modernScrollColumnStyle}
                    >
                        <style>{`
                            @keyframes copy-click-bump {
                                0% { transform: scale(1); }
                                35% { transform: scale(2.52); }
                                100% { transform: scale(1); }
                            }
                            .copy-click-bump {
                                display: inline-flex;
                                align-items: center;
                                justify-content: center;
                                transform-origin: center;
                            }
                            .copy-click-bump.is-animating {
                                animation: copy-click-bump 240ms ease-out;
                            }
                        `}</style>
                        <div style={modernStickyCardStyle}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Identity</h5>
                                <div className="d-flex align-items-center justify-content-end gap-2" style={{ minWidth: '28rem' }}>
                                    <div className="text-muted small d-flex align-items-center gap-1">
                                        <span>Layer {fdata.layer} · ID {fdata.fid}</span>
                                        <Button
                                            variant="link"
                                            className={`text-muted copy-click-bump ${idCopyAnimating ? 'is-animating' : ''}`}
                                            size="sm"
                                            title="Copy fingerprint ID"
                                            aria-label="Copy fingerprint ID"
                                            style={{ padding: '0.05rem 0.3rem', lineHeight: 1.1, textDecoration: 'none' }}
                                            onClick={() => copyFingerprintId()}
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </Button>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                        <div
                                            className="small font-monospace"
                                            style={{
                                                color: duplicateActive ? (duplicateTextColor || '#dc3545') : (theme.colors.textMuted || '#98a2b3'),
                                                wordBreak: 'break-all'
                                            }}
                                        >
                                            PvKey: {fdata.pvkey}
                                        </div>
                                        <Button
                                            variant="link"
                                            className={`text-muted copy-click-bump ${pvkeyCopyAnimating ? 'is-animating' : ''}`}
                                            size="sm"
                                            title="Copy PvKey"
                                            aria-label="Copy PvKey"
                                            style={{ padding: '0.05rem 0.3rem', lineHeight: 1.2, textDecoration: 'none' }}
                                            onClick={() => copyPvkey()}
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <Row>
                                <Form.Group as={Col} md={6} className="mb-3" controlId="m_vendor">
                                    <Form.Label>Vendor</Form.Label>
                                    <Form.Control style={{ ...modernInputStyle, fontSize: '1.5rem' }} type="text" value={fdata.vendor || ''} onChange={(e) => setFdata({ ...fdata, vendor: e.target.value })} />
                                </Form.Group>
                                <Form.Group as={Col} md={6} className="mb-3" controlId="m_product">
                                    <Form.Label>Product</Form.Label>
                                    <Form.Control style={{ ...modernInputStyle, fontSize: '1.5rem' }} type="text" value={fdata.product || ''} onChange={(e) => setFdata({ ...fdata, product: e.target.value })} />
                                </Form.Group>
                            </Row>
                            <Row>
                                <Form.Group as={Col} md={6} className="mb-3" controlId="m_vendor_tag">
                                    <Form.Label>Vendor Tag</Form.Label>
                                    <Form.Control
                                        className="font-monospace"
                                        style={{ ...modernInputStyle, ...(duplicateFieldStyle || {}) }}
                                        isInvalid={duplicateActive}
                                        type="text"
                                        value={fdata.vendor_tag || ''}
                                        onChange={(e) => {
                                            setDuplicateConflict(null);
                                            setFdata({
                                                ...fdata,
                                                pvkey: e.target.value + '__' + fdata.product_tag,
                                                vendor_tag: e.target.value
                                            });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} md={6} className="mb-3" controlId="m_product_tag">
                                    <Form.Label>Product Tag</Form.Label>
                                    <Form.Control
                                        className="font-monospace"
                                        style={{ ...modernInputStyle, ...(duplicateFieldStyle || {}) }}
                                        isInvalid={duplicateActive}
                                        type="text"
                                        value={fdata.product_tag || ''}
                                        onChange={(e) => {
                                            setDuplicateConflict(null);
                                            setFdata({
                                                ...fdata,
                                                pvkey: fdata.vendor_tag + '__' + e.target.value,
                                                product_tag: e.target.value
                                            });
                                        }}
                                    />
                                </Form.Group>
                            </Row>
                            {duplicateAlert()}
                        </div>

                        <div style={modernCardStyle}>
                            <Form.Group className="mb-3" controlId="m_title">
                                <Form.Label>Title</Form.Label>
                                <Form.Control style={modernInputStyle} type="text" value={fdata.title || ''} onChange={(e) => setFdata({ ...fdata, title: e.target.value })} />
                            </Form.Group>
                            <Form.Group controlId="m_description">
                                <Form.Label>Description</Form.Label>
                                <Form.Control style={modernInputStyle} as="textarea" rows={6} value={fdata.description || ''} onChange={(e) => setFdata({ ...fdata, description: e.target.value })} />
                            </Form.Group>
                        </div>

                        {renderModernCategoryAndMatchSection()}
                    </Form>
                </Col>

                <Col md={4} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
                    <div className="pt-2 pt-md-3 pb-2 pb-md-3 ps-2 ps-md-2 pe-2 pe-md-3" style={modernScrollColumnStyle}>
                        <div style={{ ...modernStickyCardStyle, padding: '0.7rem 0.9rem' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Actions</h6>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    title="Return to the legacy editor layout without losing the edits currently in memory."
                                    aria-label="Return to old editor"
                                    style={{
                                        minWidth: '1.85rem',
                                        width: '6.85rem',
                                        height: '1.20rem',
                                        padding: 0,
                                        lineHeight: 1,
                                        flexShrink: 0
                                    }}
                                    disabled={!modeLoaded}
                                    onClick={() => switchEditorMode('legacy')}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    old editor
                                </Button>
                            </div>
                            <div className="d-flex flex-nowrap gap-2 align-items-center" style={{ overflowX: 'auto' }}>
                                <Button
                                    variant={saveable ? 'success' : 'outline-secondary'}
                                    size="sm"
                                    title="Save the fingerprint, tags, matches, and category changes to the database."
                                    style={{ minHeight: '2.1rem' }}
                                    disabled={!saveable}
                                    onClick={() => doSave()}
                                >
                                    <FontAwesomeIcon icon={faFloppyDisk} className="me-1" />
                                    Save
                                </Button>
                                <Button
                                    variant={saveable ? 'success' : 'outline-secondary'}
                                    size="sm"
                                    title="Revert all unsaved changes back to the last saved state."
                                    style={{ minHeight: '2.1rem' }}
                                    disabled={!saveable}
                                    onClick={() => doRevert()}
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                                    Revert
                                </Button>
                                {fingerActions.filter(a => a.key === 'dup' || a.key === 'delete').map((action) => (
                                    <Button
                                        key={action.key}
                                        variant={action.variant}
                                        size="sm"
                                        title={action.help}
                                        style={{ minHeight: '2.1rem' }}
                                        onClick={action.onClick}
                                    >
                                        <FontAwesomeIcon icon={action.icon} className="me-1" />
                                        {action.label}
                                    </Button>
                                ))}
                                <div className="flex-grow-1" />
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    title="Discard changes and return to the fingerprints list."
                                    style={{ minHeight: '2.1rem' }}
                                    onClick={() => doCancel()}
                                >
                                    <FontAwesomeIcon icon={faXmark} className="me-1" />
                                    Cancel
                                </Button>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
                                gap: '1rem',
                                marginBottom: '0.85rem'
                            }}
                        >
                            <div style={{ ...modernCardStyle, padding: '0.7rem 0.9rem', marginBottom: 0 }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">Automation</h6>
                                    <span className="text-muted small">AI</span>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {fingerActions.filter(a => a.key === 'pvupdate' || a.key === 'pvupdate2').map((action) => (
                                        <Button
                                            key={action.key}
                                            variant={action.variant}
                                            size="sm"
                                            title={action.help}
                                            style={{ minHeight: '2.1rem' }}
                                            onClick={action.onClick}
                                        >
                                            <FontAwesomeIcon icon={action.icon} className="me-1" />
                                            {action.label}
                                        </Button>
                                    ))}
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        title="View probe activity for this fingerprint."
                                        style={{ minHeight: '2.1rem' }}
                                        onClick={() => navigate(`/fingerprint/${fid}/probes`)}
                                    >
                                        <FontAwesomeIcon icon={faVial} className="me-1" />
                                        Probes
                                    </Button>
                                </div>
                            </div>
                            <div style={{ ...modernCardStyle, padding: '0.7rem 0.9rem', marginBottom: 0 }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">Working Set</h6>
                                    <span className={`badge ${fdata.backendSelected === true ? 'text-bg-success' : 'text-bg-secondary'}`}>
                                        {fdata.backendSelected === true ? 'Included' : 'Excluded'}
                                    </span>
                                </div>
                                <div className="text-muted small mb-2">
                                    Controls whether this fingerprint is part of the backend working set used for focused operations.
                                </div>
                                <Form.Check
                                    type="switch"
                                    checked={fdata.backendSelected === true}
                                    onChange={(e) => {
                                        changeWorkingSet(e.target.checked);
                                    }}
                                    label={fdata.backendSelected === true ? 'Working set enabled' : 'Working set disabled'}
                                />
                            </div>
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Company Address</h5>
                            <div className="text-muted small mb-3">Edits the <span className="font-monospace">address</span>, <span className="font-monospace">country</span>, and <span className="font-monospace">country_code</span> values in Tags JSON.</div>
                            <Form.Group className="mb-3" controlId="m_company_address">
                                <Form.Label>Address</Form.Label>
                                <Form.Control
                                    style={modernInputStyle}
                                    as="textarea"
                                    rows={2}
                                    value={String(resTags.address || '')}
                                    onChange={(e) => updateResTagField('address', e.target.value)}
                                />
                            </Form.Group>
                            <Row>
                                <Form.Group as={Col} md={8} className="mb-0" controlId="m_company_country">
                                    <Form.Label>Country</Form.Label>
                                    <Form.Control
                                        style={modernInputStyle}
                                        type="text"
                                        value={String(resTags.country || '')}
                                        onChange={(e) => updateResTagField('country', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} md={4} className="mb-0" controlId="m_company_country_code">
                                    <Form.Label>Country Code</Form.Label>
                                    <style>{`.country-code-toggle::after { display: none !important; }`}</style>
                                    <Dropdown
                                        show={showCountryCodeDropdown}
                                        onToggle={(nextShow) => {
                                            if (!nextShow) {
                                                setCountryCodeFilter('');
                                            }
                                            setShowCountryCodeDropdown(Boolean(nextShow));
                                        }}
                                        drop={countryCodeDropDirection}
                                        align={countryCodeDropAlign}
                                    >
                                        <Dropdown.Toggle
                                            ref={countryCodeToggleRef}
                                            as={Button}
                                            variant="outline-secondary"
                                            className="w-100 country-code-toggle d-flex align-items-center justify-content-between font-monospace"
                                            style={{ ...modernInputStyle, minHeight: '2.375rem', textAlign: 'left' }}
                                            onClick={handleCountryCodeToggleClick}
                                        >
                                            <span>{currentCountryCode || 'Select code'}</span>
                                            <span className="text-muted small ms-2 text-truncate">
                                                {selectedCountryCodeOption ? selectedCountryCodeOption.name : (currentCountryCode ? 'Custom code' : 'Search')}
                                            </span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu
                                            style={{
                                                width: '100%',
                                                minWidth: '20rem',
                                                maxHeight: '320px',
                                                border: '1px solid var(--bs-info)',
                                                borderRadius: '0.375rem',
                                                boxShadow: '0 0 0 0.2rem rgba(13, 202, 240, 0.12)',
                                                padding: 0,
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '320px', minHeight: 0 }}>
                                                {countryCodeDropDirection === 'down' ? (
                                                    <div className="px-3 py-2">
                                                        <Form.Control
                                                            ref={countryCodeFilterRef}
                                                            type="text"
                                                            placeholder="Filter countries or enter code..."
                                                            value={countryCodeFilter}
                                                            onChange={(e) => setCountryCodeFilter(e.target.value.toUpperCase())}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && customCountryCode.length === 2) {
                                                                    e.preventDefault();
                                                                    setCountryCode(customCountryCode);
                                                                }
                                                            }}
                                                            size="sm"
                                                            className="w-100 font-monospace"
                                                            maxLength={2}
                                                        />
                                                    </div>
                                                ) : null}
                                                <div
                                                    style={{
                                                        flex: '1 1 auto',
                                                        minHeight: 0,
                                                        overflowY: 'auto',
                                                        overscrollBehavior: 'contain'
                                                    }}
                                                    onWheel={handleCountryCodeListWheel}
                                                >
                                                    {showCustomCountryCodeOption ? (
                                                        <Dropdown.Item onClick={() => setCountryCode(customCountryCode)}>
                                                            Use <span className="font-monospace">{customCountryCode}</span>
                                                        </Dropdown.Item>
                                                    ) : null}
                                                    {filteredCountryCodeOptions.map((option) => (
                                                        <Dropdown.Item key={option.code} onClick={() => setCountryCode(option.code)}>
                                                            <span className="font-monospace me-2">{option.code}</span>
                                                            <span>{option.name}</span>
                                                        </Dropdown.Item>
                                                    ))}
                                                    {filteredCountryCodeOptions.length === 0 && !showCustomCountryCodeOption ? (
                                                        <Dropdown.Item disabled className="py-2">
                                                            No country codes match
                                                        </Dropdown.Item>
                                                    ) : null}
                                                </div>
                                                {countryCodeDropDirection === 'up' ? (
                                                    <div className="px-3 py-2">
                                                        <Form.Control
                                                            ref={countryCodeFilterRef}
                                                            type="text"
                                                            placeholder="Filter countries or enter code..."
                                                            value={countryCodeFilter}
                                                            onChange={(e) => setCountryCodeFilter(e.target.value.toUpperCase())}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && customCountryCode.length === 2) {
                                                                    e.preventDefault();
                                                                    setCountryCode(customCountryCode);
                                                                }
                                                            }}
                                                            size="sm"
                                                            className="w-100 font-monospace"
                                                            maxLength={2}
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Form.Group>
                            </Row>
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Geo</h5>
                            <div className="text-muted small mb-3">Uses <span className="font-monospace">country_code</span> as primary map country highlight and plots company coordinates when present.</div>
                            <Row className="g-2 align-items-end">
                                <Col md={4}>
                                    <Form.Label className="small mb-1">Lat</Form.Label>
                                    <Form.Control
                                        style={modernInputStyle}
                                        className="font-monospace"
                                        size="sm"
                                        value={String(resTags.latitude ?? resTags?.geo?.lat ?? '')}
                                        placeholder="n/a"
                                        onChange={(e) => updateResTagField('latitude', e.target.value)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Label className="small mb-1">Lon</Form.Label>
                                    <Form.Control
                                        style={modernInputStyle}
                                        className="font-monospace"
                                        size="sm"
                                        value={String(resTags.longitude ?? resTags?.geo?.lon ?? '')}
                                        placeholder="n/a"
                                        onChange={(e) => updateResTagField('longitude', e.target.value)}
                                    />
                                </Col>
                                <Col md={4} className="d-grid">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        style={{ minHeight: '2rem' }}
                                        disabled={!canOpenGeoMap}
                                        onClick={() => setShowGeoModal(true)}
                                    >
                                        Map
                                    </Button>
                                </Col>
                            </Row>
                            <div className="small text-muted mt-2">
                                Country: <span className="font-monospace">{geoCountryCode || '--'}</span>
                                {selectedCountryCodeOption?.name ? ` (${selectedCountryCodeOption.name})` : ''}
                            </div>
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Analyst Notes</h5>
                            <Form.Group controlId="m_analyst_notes">
                                <Form.Control
                                    style={modernInputStyle}
                                    as="textarea"
                                    rows={5}
                                    value={String(resTags.notes || '')}
                                    onChange={(e) => updateResTagField('notes', e.target.value)}
                                    placeholder="Add analyst notes..."
                                />
                            </Form.Group>
                        </div>

                        <div style={modernCardStyle}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Intermediate Host Names</h5>
                                <span className="badge text-bg-secondary">{intermediateHostEntries.length}</span>
                            </div>
                            <div className="text-muted small mb-3">
                                Used to manually discover additional base DNS names for the company (or remove bad ones) before running update.
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                                <div style={{ flex: '1 1 16rem' }}>
                                    <Form.Label className="small mb-1">Host name</Form.Label>
                                    <Form.Control
                                        style={modernInputStyle}
                                        className="font-monospace"
                                        size="sm"
                                        type="text"
                                        value={intermediateHostInput}
                                        placeholder="api.example.com"
                                        onChange={(e) => setIntermediateHostInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addIntermediateHost();
                                            }
                                        }}
                                    />
                                </div>
                                <Form.Check
                                    id="m_intermediate_host_api"
                                    type="switch"
                                    className="mb-1"
                                    label="API"
                                    checked={intermediateHostApi}
                                    onChange={(e) => setIntermediateHostApi(e.target.checked)}
                                />
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    style={{ minHeight: '2rem' }}
                                    onClick={() => addIntermediateHost()}
                                >
                                    Add
                                </Button>
                            </div>
                            {intermediateHostEntries.length === 0 ? (
                                <div className="text-muted small">No intermediate host names configured.</div>
                            ) : (
                                <BTable hover responsive size="sm" className="mb-0">
                                    <tbody>
                                        {intermediateHostEntries.map((entry) => (
                                            <tr key={entry.host}>
                                                <td className="font-monospace" style={{ fontSize: '0.8rem' }}>{entry.host}</td>
                                                <td style={{ width: '1px', whiteSpace: 'nowrap' }}>
                                                    <Form.Check
                                                        id={`m_intermediate_row_${entry.host}`}
                                                        type="switch"
                                                        className="m-0"
                                                        aria-label={`Toggle api for ${entry.host}`}
                                                        checked={entry.api}
                                                        onChange={(e) => setIntermediateHostApiFlag(entry.host, e.target.checked)}
                                                    />
                                                </td>
                                                <td style={{ width: '1px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        style={{ padding: '0.1rem 0.3rem', lineHeight: 1 }}
                                                        onClick={() => removeIntermediateHost(entry.host)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashCan} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </BTable>
                            )}
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Flags</h5>
                            <div className="d-flex justify-content-between align-items-center gap-3">
                                <span className="font-monospace">yeet_pending</span>
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge ${hasYeetPending ? (yeetPending ? 'text-bg-warning' : 'text-bg-secondary') : 'text-bg-dark'}`}>
                                        {yeetPendingStateLabel}
                                    </span>
                                    <Form.Check
                                        id="m_flag_yeet_pending"
                                        type="switch"
                                        className="m-0"
                                        aria-label="Toggle yeet_pending"
                                        checked={yeetPending}
                                        onChange={(e) => updateResTagBooleanField('yeet_pending', e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Last Updates</h5>
                            <div className="text-muted small mb-3">Derived from timestamp fields in Tags JSON. Clearing a row removes that key from <span className="font-monospace">res_tags</span>.</div>
                            {lastUpdateEntries.length === 0 ? (
                                <div className="text-muted small">No last update timestamps present.</div>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {lastUpdateEntries.map(([key, value]) => {
                                        const daysSince = getDaysSince(value);
                                        return (
                                            <div key={key} className="d-flex align-items-start justify-content-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="fw-semibold">{formatLastUpdateLabel(key)}</div>
                                                    <div className="small text-muted font-monospace" style={{ wordBreak: 'break-all' }}>{value}</div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                    <span className={`badge ${daysSince === null ? 'text-bg-secondary' : 'text-bg-info'}`}>
                                                        {daysSince === null ? 'n/a' : `${daysSince}d`}
                                                    </span>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => clearResTagField(key)}
                                                    >
                                                        Clear
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Tags JSON</h5>
                            <div className="text-muted small mb-2">Single source of truth for tags.</div>
                            <JsonEditor
                                {...sharedJsonEditorProps}
                                data={fdata.res_tags}
                                rootName="res_tags"
                                onUpdate={({ newData }) => {
                                    setResTagsData(newData as any)
                                }}
                            />
                        </div>

                        <div style={modernCardStyle}>
                            <h5 className="mb-2">Presentation Overrides</h5>
                            <div className="text-muted small mb-2">Single source of truth for presentation overrides.</div>
                            <JsonEditor
                                {...sharedJsonEditorProps}
                                data={fdata.presentation}
                                rootName="presentation"
                                onUpdate={({ newData }) => {
                                    setPresentationData(newData as any)
                                }}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }

    return (
        <>
            <Container>
                {editorMode !== 'modern' && (
                <Row className="pt-2 mb-3 border-bottom border-2">
                    <div className="col-6">
                        <h3 className="">{fdata.vendor + ", " + fdata.product}</h3>
                    </div>
                    <div className="col-6 mb-2">
                        <ButtonToolbar className="float-end me-4">
                            <>
                            <Form.Check
                                type="switch"
                                checked={fdata.backendSelected === true}
                                onChange={(e) => {
                                    changeWorkingSet(e.target.checked);
                                }}
                                label='Working Set'
                                className="me-2 mt-1"
                            ></Form.Check>
                            <Button
                                variant="outline-info"
                                size="sm"
                                title={topActionLabel}
                                className="me-2"
                                disabled={!modeLoaded}
                                onClick={() => {
                                    switchEditorMode('modern');
                                }}
                            >
                                <FontAwesomeIcon icon={topActionIcon} className="me-1" />
                                {topActionLabel}
                            </Button>
                            <Dropdown as={ButtonGroup} className="me-2">
                                <Dropdown.Toggle id="finger-actions-dropdown" variant="success">
                                    Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {fingerActions.map((action) => (
                                        <Dropdown.Item
                                            key={action.key}
                                            title={action.help}
                                            onClick={action.onClick}
                                        >
                                            <FontAwesomeIcon icon={action.icon} className="me-2"/>
                                            {action.menuLabel}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            <Button variant={saveable ? 'danger' : 'success'} title="Save Fingerprint"
                                disabled={!saveable}
                                onClick={() => {
                                        doSave();
                                    }
                                } >
                                <FontAwesomeIcon icon={faFloppyDisk} />
                            </Button>
                            </>
                        </ButtonToolbar>
                    </div>
                </Row>
                )}
                {/* Transition switch: remove legacy renderer and /display/fingerEditMode once modern editor is fully adopted. */}
                {editorMode === 'modern' ? renderModernEditor() : renderLegacyEditor()}
            </Container>

            <Modal
                show={showGeoModal}
                onHide={() => setShowGeoModal(false)}
                centered
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex flex-column">
                        <span>Geo Map</span>
                        <span className="small text-muted fw-normal">
                            <span className="font-monospace">{fdata.pvkey || '--'}</span>
                            {' | '}
                            {fdata.vendor || '--'}
                            {' / '}
                            {fdata.product || '--'}
                        </span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex gap-3 flex-wrap mb-2 small text-muted">
                        <span>Country code: <span className="font-monospace">{geoCountryCode || '--'}</span></span>
                        <span>Lat: <span className="font-monospace">{hasGeoCoordinates ? String(geoLat) : 'n/a'}</span></span>
                        <span>Lon: <span className="font-monospace">{hasGeoCoordinates ? String(geoLon) : 'n/a'}</span></span>
                    </div>
                    <div
                        style={{
                            width: '100%',
                            height: '60vh',
                            minHeight: '24rem',
                            borderRadius: 8,
                            overflow: 'hidden',
                            border: `1px solid ${theme.colors.border || '#2a3140'}`,
                            backgroundColor: theme.colors.mapBg || '#11131a'
                        }}
                    >
                        <FingerGeoMap
                            lat={geoLat}
                            lon={geoLon}
                            isCountryMatch={geoIsCountryMatch}
                            highlightColor={theme.colors.info || '#0dcaf0'}
                            strokeColor={theme.colors.mapStroke || '#4b5563'}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGeoModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <AddMatchDialog
                show={showInsertMatchDialog}
                setShow={setShowInsertMatchDialog}
                editFp={fdata}
                setEditFp={setFdata}
                editMatchIndex={editMatchIndex}
                setEditMatchIndex={setEditMatchIndex}
            />
        </>
    )
}

export default FingerEdit;